/**
 * Collection contains saved search models
 * @class
 */
import {currentUserIsAdmin} from '@netx/core-permissions-next/enablers/currentUserIsAdmin';
import AssetSetModel from '@netx/core-asset/models/assetSet';
import {types as assetSetTypes} from '@netx/core-asset/properties/assetSets';
import Base from '@netx/core-share/collections/sharedResources';
import {userProfile} from '@netx/core-user/collections/singletons/userProfile';
import Model from '../models/savedSearch';

export default class SavedSearches extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/** @type {object} */
	get xhrLoadingMessages() {
		return {
			getSearchesForUser: 'Loading saved searches',
			getSharedResourcesForUser() {
				return this.isFiltered()
					? 'Filtering saved searches'
					: 'Loading saved searches';
			},
		};
	}

	/** @type {object} */
	get xhrLoadedMessages() {
		return {
			getSearchesForUser: 'Loaded saved searches',
			getSharedResourcesForUser() {
				return this.isFiltered()
					? 'Filtered saved searches'
					: 'Loaded saved searches';
			},
		};
	}

	/**
	 * @property {object} methods                    - Methods for this collection
	 * @property {method} methods.fetchBeans         - Fetch saved search beans
	 * @property {method} methods.fetchBeansForUser  - Fetch saved search beans for user
	 */
	get methods() {
		return _.extend(super.methods, {
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			fetchBeans(opts) {
				return this.fetchBeansForUser(userProfile.id, opts);
			},
			/**
			 * @method
			 * @param {number} userId User id
			 * @param {object} opts Request options
			 */
			fetchBeansForUser(userId, opts) {
				if (!currentUserIsAdmin()) userId = userProfile.id;

				return [
					'getSearchesForUser',
					userId,
					this.modeFilters,
					this.filterStr,
					this.pager.getPageDTO(),
				];
			},
		});
	}

	/**
	 * @property {object} parsers                    - Parsers for this collection
	 * @property {method} parsers.fetchBeans         - Parse response of saved search beans
	 * @property {method} parsers.fetchBeansForUser  - Parse response of saved search beans
	 */
	get parsers() {
		return _.extend(super.parsers, {
			/**
			 * @function
			 * @param {array} data Saved carts
			 * @param {object} opts Request options
			 */
			fetchBeans(data, opts) {
				this.set(data, _.extend({merge: true}, opts));

				!opts.silent && this.trigger('sync', this, data, opts);
			},
			/**
			 * @function
			 * @param {array} data Saved carts
			 * @param {number} userId User id
			 * @param {object} opts Request options
			 */
			fetchBeansForUser(data, userId, opts) {
				this.set(data, opts);

				!opts.silent && this.trigger('sync', this, data, opts);
			},
		});
	}

	/**
	 * @override
	 * @listens module:search#search:saved:delete:success
	 * @listens module:search#search:save:success
	 */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;
		opts.resourceName = opts.resourceName || 'savedSearch';

		super(models, opts);

		// Set name filter if provided
		this.filterStr = opts.filterStr || '';

		// Hey, listen!
		this.listenTo(dispatcher, 'search:saved:delete:success', (savedSearch) => {
			const model = this.get(savedSearch.id);
			model && this.remove(model);
		}).listenTo(
			dispatcher,
			'search:save:success',
			(savedSearches, savedSearch) => {
				this.matchesFilters(savedSearch) &&
					this.unshift(savedSearch.attributes);
			},
		);
	}

	/**
	 * @method
	 * @return {module:asset/models/assetSet}
	 */
	toAssetSet() {
		return new AssetSetModel({
			type: assetSetTypes.search,
			ids: this.ids(),
			label: i18n.t('i18n.savedSearches'),
		});
	}

	/** @type {string} */
	static get __name__() {
		return 'SavedSearches';
	}
}
